/**
 * Inbox service: items come in (files, bank rows), the pipeline proposes,
 * the user decides. Booking composes account activation (numbers the UI
 * confirmed), `compileTransaction`, `bookVerifikation` with the
 * plain-language `meta`, bank row links, Drive filing, merchant memory and
 * the backup hook – all in one Dexie transaction up to and including the
 * booking, so a failure leaves the item reopened with a Swedish reason and
 * nothing half-written.
 *
 * Inbox items, bank row links and merchant rules are working state; the
 * verifikation is the legal record and is never touched after booking.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { AccountRuleViolation, activateBasAccount } from '../accounts/service';
import { storeAttachment } from '../attachments/service';
import { rankCandidates, decide, reconciledText } from '../bank/match';
import type { Candidate } from '../bank/match';
import { REIMBURSABLE_OWNER_ACCOUNT, deriveOpenItems, foreignCurrencyIdsFrom, isOpenInvoiceShape, refundablePurchases, reimbursementOwed } from '../bank/open-items';
import { COMBINATION_OPTIONS, MATCH_OPTIONS, MONEY_IN_OPTIONS, RECEIPT_OPTIONS, REFUND_OPTIONS, TAX_OPTIONS, TRANSFER_OPTIONS, taxQuestion } from '../bank/questions';
import { findCategory } from '../catalog/rules';
import { isWithin, rulesYearOf } from '../fiscal-years/service';
import { formatSek, type Ore } from '../money';
import { merchantKeyOf } from '../merchants/normalize';
import { rememberMerchant, ruleDecision, findMerchantRule } from '../merchants/service';
import type { RememberResult } from '../merchants/service';
import { periodKeyFor } from '../periods/keys';
import { isLedgerError, isProviderError, withSharedLedger } from '../sync/shared-ledger';
import { compileTransaction } from '../transactions/compile';
import { CompileError, type CompileResult, type TransactionInput } from '../transactions/types';
import type {
  Account,
  DecidedField,
  InboxItem,
  InboxStatus,
  IsoDateTime,
  MerchantRule,
  PaymentSource,
  Proposal,
  ProposalAllocation,
  ProposalQuestion,
  TaxSubkind,
  TransactionKind,
  Uuid,
  VatRate,
  Verifikation,
  VerifikationMeta,
} from '../types';
import { VerifikationRuleViolation, bookVerifikation } from '../verifikationer/service';
import type { InboxDeps, InboxGroup, InboxProposal, ProcessSummary } from './internal-types';
import {
  BANK_ROW_MISSING,
  INVOICE_SETTLEMENT_REASON,
  MATCH_NONE_VALUE,
  REIMBURSEMENT_REASON,
  cardQuestion,
  ensureBankRowItems,
  fileLateDocument,
  grossOf,
  isDecided,
  kindBooksOnCategory,
  linkRow,
  loadCompanyContext,
  patchItem,
  paymentSourceForHint,
  retireBankRowItems,
  taxSubkindOf,
  triageInput,
  withDecided,
  type CompanyContext,
  type ItemPatch, FILING_FAILED, fileBookedReceipt } from './pipeline';
import { reconcileCompany } from './reconcile';
import { runPipeline } from './run';
import { TRIAGE_REASON, batchEligible, triage } from './triage';
import type { BookBatchInput, BookBatchResult, BookItemInput, InboxFileInput, InboxService, OpenItem, ProcessOptions, SkipReason } from './types';

// ---- Copy -------------------------------------------------------------------------

export const SKIP_REASON_TEXT: Record<SkipReason, string> = {
  not_receipt: 'Inte ett kvitto',
  duplicate: 'Dubblett – hoppades över',
  private: 'Privat – inte företagets',
};

export const BOOKING_ERROR = {
  itemMissing: 'Posten finns inte längre i Att göra.',
  alreadyBooked: 'Det här är redan bokfört.',
  outsideFiscalYear: 'Datumet ligger utanför ett öppet räkenskapsår.',
  /** The spec's ActivateAccountSheet names the account, so the number may appear here too. */
  activationNotConfirmed: (numbers: number[]) =>
    numbers.length === 1 ? `Kontot ${numbers[0]} behöver läggas till i kontoplanen först.` : `Kontona ${listSwedish(numbers.map(String))} behöver läggas till i kontoplanen först.`,
  incompleteProposal: 'Förslaget är inte komplett – öppna posten och fyll i det som saknas.',
  batchNeedsYou: 'Poster som behöver dig bokförs en i taget.',
  batchAboveThreshold: (thresholdOre: Ore) => `Belopp över ${formatSek(thresholdOre)} kr bokförs en i taget.`,
  driveFiling: FILING_FAILED,
  generic: 'Något gick fel när händelsen skulle bokföras. Försök igen.',
  splitSum: 'Delarna måste tillsammans bli samma belopp som kvittot.',
  splitParts: 'Dela upp i minst två delar med belopp över noll.',
  cannotReopenBooked: 'Bokförda händelser ändras med en rättelse, inte genom att öppnas igen.',
  /** Spec section 9: a company bank row is never ignored. */
  bankRowNeverIgnored: 'En rad på företagets konto kan inte hoppas över. Bokför den, eller välj "Det här var privat" så bokförs den mot dig.',
  paybackTooLarge: (owedOre: Ore) => `Beloppet är större än det utlägg som är kvar att betala tillbaka (${formatSek(owedOre)} kr).`,
  paybackFromOwner: 'En återbetalning av utlägg betalas från ett av företagets konton.',
  verifikationMissing: 'Händelsen finns inte.',
  /** The item's own bank row was booked by something else while the card was open. */
  bankRowAlreadyBooked: 'Banktransaktionen är redan bokförd. Ladda om Att göra så ser du hur.',
  /** A row this receipt was matched to has since been linked to another booking. */
  bankRowTaken: (label: string) => `Banktransaktionen är redan kopplad till ${label}. Kontrollera matchningen innan du bokför.`,
  /** The allocations do not add up to the amount booked. */
  allocationSum: 'Fördelningen på fakturorna stämmer inte med beloppet.',
  /** More would be settled than the invoice still owes. */
  allocationTooLarge: (label: string, remainingOre: Ore) => `Fakturan ${label} har bara ${formatSek(remainingOre)} kr kvar att betala.`,
  refundTooLarge: (label: string, remainingOre: Ore) => `Köpet ${label} kan bara återbetalas med ${formatSek(remainingOre)} kr till.`,
  mergeSelf: 'Ett underlag kan inte läggas till sig självt.',
  mergeClosed: 'Underlag kan bara läggas till en händelse som inte är bokförd än.',
} as const;

/** Answers to the one-question cards (`answerQuestion`). */
export const ANSWER_TEXT = {
  unknownChoice: 'Okänt val.',
  unknownTax: 'Vet inte – öppna posten och välj vad betalningen till Skatteverket gällde, eller fråga din konsult.',
  noInvoiceFound: 'Hittade ingen obetald faktura som stämmer – välj den under Ändra.',
  noCandidateFound: 'Hittade inget kvitto att koppla till längre – bokför raden som ett köp.',
  /** "Ingen av dem" on the receipt's payment question. */
  noRowChosen: 'Ingen av banktransaktionerna betalade köpet – det bokförs som väntande betalning.',
} as const;

/** Reasons written on a receipt's card by the user's own decisions. */
export const DECISION_TEXT = {
  matchConfirmed: 'Kopplingen till banktransaktionen är bekräftad.',
  matchRejected: 'Du sa att banktransaktionen inte hör hit.',
  /** "Underlag till samma köp som …". */
  mergedInto: (title: string) => `Underlag till samma köp som ${title}`,
  merged: (n: number) => `${n} ${n === 1 ? 'underlag till' : 'underlag till'} samma köp`,
} as const;

function listSwedish(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} och ${parts[parts.length - 1]}`;
}

const wholeSekFormatter = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });

/** "14 380" – whole kronor for group headings (the spec shows totals without öre). */
export function formatWholeSek(amount: Ore): string {
  return wholeSekFormatter.format(Math.round(amount / 100));
}

export class InboxBookingError extends Error {
  constructor(
    message: string,
    public readonly technical?: string,
  ) {
    super(message);
    this.name = 'InboxBookingError';
  }
}

/** Swedish sentence for whatever booking threw. */
export function bookingErrorMessage(err: unknown): { message: string; technical?: string } {
  if (err instanceof InboxBookingError) return { message: err.message, ...(err.technical ? { technical: err.technical } : {}) };
  if (err instanceof CompileError) return { message: err.message };
  if (err instanceof VerifikationRuleViolation) return { message: err.message };
  if (err instanceof AccountRuleViolation) return { message: err.message };
  // A shared bookkeeping that could not reach, or agree with, the remote copy.
  if (isLedgerError(err)) return { message: err.message };
  if (isProviderError(err)) return { message: err.message, technical: err.technical };
  const technical = err instanceof Error ? err.message : String(err);
  return { message: BOOKING_ERROR.generic, technical };
}

const now = (): IsoDateTime => new Date().toISOString();

// ---- Pure helpers -------------------------------------------------------------------

/** Statuses an item can be booked, skipped or reopened from. */
export const OPEN_STATUSES: ReadonlySet<InboxStatus> = new Set<InboxStatus>(['new', 'processing', 'ready', 'check', 'needs_you', 'error']);

/** The status an item returns to when its booking fails or the user reopens it. */
export function reopenedStatus(item: Pick<InboxItem, 'tier' | 'proposal'>): InboxStatus {
  return item.tier ?? (item.proposal ? 'check' : 'needs_you');
}

/** Every document an item books with: its own file and the ones folded into it. */
export function documentIdsOf(item: Pick<InboxItem, 'attachmentId' | 'extraAttachmentIds'>): string[] {
  return [...new Set([...(item.attachmentId ? [item.attachmentId] : []), ...(item.extraAttachmentIds ?? [])])];
}

/**
 * Build the `TransactionInput` a proposal describes; null when something the
 * compiler needs is missing. A settlement or refund takes its target from the
 * proposal's allocations (the first one; `bookCore` books the rest), the
 * caller's `extra` only filling in what the proposal does not say.
 */
export function transactionInputFromItem(item: InboxItem, extra: { counterpartySourceId?: Uuid | undefined; settlesVerifikationId?: Uuid | undefined } = {}): TransactionInput | null {
  const p = item.proposal;
  if (!p || p.amountOre === null || p.amountOre <= 0 || p.date === null || !p.paymentSourceId) return null;
  if (p.question) return null;
  const input: TransactionInput = {
    kind: p.kind,
    paymentSourceId: p.paymentSourceId,
    amountOre: p.amountOre,
    date: p.date,
    counterparty: p.counterparty,
    documentIds: documentIdsOf(item),
  };
  if (p.categoryId) input.categoryId = p.categoryId;
  if (p.vat) input.vat = p.vat;
  const taxSubkind = taxSubkindOf(p);
  if (taxSubkind) input.taxSubkind = taxSubkind;
  if (item.note) input.note = item.note;
  if (extra.counterpartySourceId) input.counterpartySourceId = extra.counterpartySourceId;
  const options: NonNullable<TransactionInput['options']> = {};
  if (p.options?.deferPayment) options.deferPayment = true;
  if (p.options?.persons !== undefined) options.persons = p.options.persons;
  if (p.options?.assetChoice) options.assetChoice = p.options.assetChoice;
  const first = p.allocations?.[0];
  if (p.kind === 'settle') {
    const target = first?.verifikationId ?? extra.settlesVerifikationId;
    if (target) options.settlesVerifikationId = target;
  }
  if (p.kind === 'refund' && first) options.refundOfVerifikationId = first.verifikationId;
  if (Object.keys(options).length) input.options = options;
  switch (p.kind) {
    case 'purchase':
    case 'sale':
      if (!p.categoryId) return null;
      break;
    case 'refund':
      if (!input.options?.refundOfVerifikationId) return null;
      break;
    case 'transfer':
      if (!input.counterpartySourceId) return null;
      break;
    case 'settle':
      if (!input.options?.settlesVerifikationId) return null;
      break;
    case 'owner_in':
    case 'owner_out':
      break;
    case 'tax':
      if (!input.taxSubkind) return null;
      break;
    case 'vat_settlement':
    case 'year_end':
    case 'other':
      return null;
    default: {
      const exhaustive: never = p.kind;
      throw new Error(`Unknown kind ${String(exhaustive)}`);
    }
  }
  return input;
}

// ---- Answers to the one-question cards ------------------------------------------------

/** What the service has to do besides updating the proposal. */
export type AnswerEffect =
  | { type: 'none' }
  /** "Ja, koppla": resolve the best candidate again (settlement → link, invoice/utlägg → settle). */
  | { type: 'link' }
  /** "Nej, visa fler": the candidate the card asked about is ruled out. */
  | { type: 'reject_candidate' }
  /** "Nej" to an own transfer: release the paired row. */
  | { type: 'release_pair' }
  /** "Betalning för en faktura": find the open invoice the row pays. */
  | { type: 'settle_target' }
  /** "Ja" to a combined payment or a refund: the proposed allocations stand. */
  | { type: 'confirm_allocations' }
  /** "Nej" to a combined payment or a refund: those allocations are ruled out. */
  | { type: 'reject_allocations' }
  /** A receipt's payment question: this bank row paid it. */
  | { type: 'match_row'; rowId: Uuid }
  /** A receipt's payment question: none of the listed rows did. */
  | { type: 'reject_rows'; rowIds: Uuid[] }
  /** A credit note's target question: this purchase is the one it reduces. */
  | { type: 'match_target'; verifikationId: Uuid }
  /** A credit note's target question: none of the listed purchases. */
  | { type: 'reject_targets'; verifikationIds: Uuid[] }
  /** The answer needs the user ("Vet inte"). */
  | { type: 'needs_you'; reason: string };

export interface AppliedAnswer {
  proposal: InboxProposal;
  effect: AnswerEffect;
  /** "Ladda upp kvitto": nothing changes until the file arrives; the question stays. */
  keepQuestion: boolean;
  /** Which proposal fields this answer settled (kept over every later reconciliation). */
  decided: DecidedField[];
}

const TRANSACTION_KINDS: readonly TransactionKind[] = ['purchase', 'sale', 'transfer', 'owner_in', 'owner_out', 'refund', 'tax', 'settle', 'vat_settlement', 'year_end', 'other'];
const TAX_SUBKIND_VALUES: Readonly<Record<string, TaxSubkind>> = { [TAX_OPTIONS.vat]: 'vat_payment', [TAX_OPTIONS.fTax]: 'f_tax', [TAX_OPTIONS.other]: 'skattekonto_deposit' };

function withoutTaxSubkind(p: InboxProposal): InboxProposal {
  const { taxSubkind: _t, ...rest } = p;
  return rest;
}

/**
 * Pure: apply one answer to a proposal. Understands the option values of every
 * section 9 card (`bank/questions`), the VAT chip and plain transaction kinds.
 * Throws `InboxBookingError` for a value no card produces.
 */
export function applyAnswer(current: Proposal, answer: { field: ProposalQuestion['field']; value: string }): AppliedAnswer {
  const { question, ...rest } = current as InboxProposal;
  let proposal: InboxProposal = { ...rest };
  let effect: AnswerEffect = { type: 'none' };
  const decided: DecidedField[] = [];
  const value = answer.value.trim();
  const withoutAllocations = (p: InboxProposal): InboxProposal => {
    const { allocations: _a, ...kept } = p;
    return kept;
  };
  switch (answer.field) {
    case 'vat': {
      const rate = Number(value) as VatRate;
      if (!Number.isInteger(rate) || ![25, 12, 6, 0].includes(rate)) throw new InboxBookingError('Välj en momssats: 25, 12, 6 eller 0 %.');
      proposal.vat = rate === 0 ? { rate, mode: 'none' } : { rate, mode: 'domestic' };
      decided.push('vat');
      break;
    }
    case 'kind': {
      const subkind = TAX_SUBKIND_VALUES[value];
      if (subkind) {
        proposal = { ...withoutAllocations(proposal), kind: 'tax', taxSubkind: subkind, categoryId: null, vat: null, counterparty: proposal.counterparty.trim() || 'Skatteverket' };
        decided.push('kind', 'taxSubkind');
      } else if (value === TAX_OPTIONS.unknown) {
        proposal = { ...withoutTaxSubkind(withoutAllocations(proposal)), kind: 'tax', categoryId: null, vat: null, reason: null, confidence: 0 };
        decided.push('kind');
        effect = { type: 'needs_you', reason: ANSWER_TEXT.unknownTax };
      } else if (value === MONEY_IN_OPTIONS.invoice) {
        proposal = { ...withoutTaxSubkind(withoutAllocations(proposal)), kind: 'settle', categoryId: null, vat: null };
        decided.push('kind');
        effect = { type: 'settle_target' };
      } else if (value === MATCH_OPTIONS.link) {
        effect = { type: 'link' };
      } else if (value === MATCH_OPTIONS.more) {
        proposal = { ...withoutAllocations(proposal), reason: null, confidence: 0 };
        effect = { type: 'reject_candidate' };
      } else if (value === COMBINATION_OPTIONS.yes || value === REFUND_OPTIONS.yes) {
        // The proposal already carries the allocations the card asked about.
        proposal = { ...withoutTaxSubkind(proposal), reason: 'match', confidence: 1 };
        decided.push('kind', 'allocations', 'match');
        effect = { type: 'confirm_allocations' };
      } else if (value === COMBINATION_OPTIONS.no || value === REFUND_OPTIONS.no) {
        proposal = { ...withoutAllocations(proposal), reason: null, confidence: 0 };
        effect = { type: 'reject_allocations' };
      } else if (value === TRANSFER_OPTIONS.yes) {
        proposal = { ...withoutTaxSubkind(withoutAllocations(proposal)), kind: 'transfer', categoryId: null, vat: null };
        decided.push('kind');
      } else if (value === TRANSFER_OPTIONS.no) {
        proposal = { ...proposal, reason: null, confidence: 0 };
        effect = { type: 'release_pair' };
      } else if (value === RECEIPT_OPTIONS.upload) {
        return { proposal: current as InboxProposal, effect, keepQuestion: true, decided };
      } else if (value === RECEIPT_OPTIONS.without) {
        proposal = { ...withoutTaxSubkind(withoutAllocations(proposal)), kind: 'purchase' };
        decided.push('kind');
      } else if (value === RECEIPT_OPTIONS.private) {
        proposal = { ...withoutTaxSubkind(withoutAllocations(proposal)), kind: 'owner_out', categoryId: null, vat: null };
        decided.push('kind');
      } else if ((TRANSACTION_KINDS as readonly string[]).includes(value)) {
        const kind = value as TransactionKind;
        proposal = kind === 'tax' ? { ...proposal, kind } : { ...withoutTaxSubkind(proposal), kind };
        if (!kindBooksOnCategory(kind)) proposal = withoutAllocations(proposal);
        decided.push('kind');
      } else {
        throw new InboxBookingError(ANSWER_TEXT.unknownChoice);
      }
      break;
    }
    case 'category':
      if (!value) throw new InboxBookingError(ANSWER_TEXT.unknownChoice);
      proposal.categoryId = value;
      decided.push('categoryId');
      break;
    case 'payment':
      if (!value) throw new InboxBookingError(ANSWER_TEXT.unknownChoice);
      proposal.paymentSourceId = value;
      decided.push('paymentSourceId');
      break;
    case 'match': {
      // Option values are ids: bank rows for a receipt's payment, purchases for a credit note.
      const offered = (question?.options ?? []).map((o) => o.value).filter((v) => v !== MATCH_NONE_VALUE);
      const isCreditNote = proposal.kind === 'refund';
      if (value === MATCH_NONE_VALUE) {
        effect = isCreditNote ? { type: 'reject_targets', verifikationIds: offered } : { type: 'reject_rows', rowIds: offered };
        if (isCreditNote) proposal = withoutAllocations(proposal);
      } else if (offered.includes(value)) {
        if (isCreditNote) {
          const amountOre = proposal.amountOre ?? 0;
          proposal = { ...proposal, allocations: [{ verifikationId: value, amountOre }], reason: 'match' };
          decided.push('allocations');
          effect = { type: 'match_target', verifikationId: value };
        } else {
          decided.push('match');
          effect = { type: 'match_row', rowId: value };
        }
      } else {
        throw new InboxBookingError(ANSWER_TEXT.unknownChoice);
      }
      break;
    }
    case 'persons':
      break;
    default: {
      const exhaustive: never = answer.field;
      throw new Error(`Unknown question field ${String(exhaustive)}`);
    }
  }
  return { proposal, effect, keepQuestion: false, decided };
}

// ---- Reimbursement payback (kvittning of "Egna pengar") -------------------------------------

export interface PaybackInput {
  input: TransactionInput;
  /** The company account the payback leaves from. */
  source: PaymentSource;
  /** The purchase paid with the owner's money (credit on 2893). */
  original: Verifikation;
  /** Paybacks already booked against it. */
  alreadyPaidOre: Ore;
  accounts: ReadonlyMap<number, Account>;
  periodKey: string;
  origin: VerifikationMeta['source'];
}

/**
 * The payback of a purchase made with the owner's money: D 2893 / K bank.
 * `compileTransaction`'s `settle` only knows 2440/1510 invoices, so this
 * local adapter builds the same `CompileResult` shape for the 2893 case
 * (meta kind `settle` + `settlesVerifikationId`, which is what closes the
 * reimbursement in `deriveOpenItems`).
 */
export function compileReimbursementPayback(p: PaybackInput): CompileResult {
  const owed = reimbursementOwed(p.original) - p.alreadyPaidOre;
  const gross = p.input.amountOre;
  if (!Number.isInteger(gross) || gross <= 0) throw new InboxBookingError('Beloppet måste vara större än noll.');
  if (p.source.kind === 'owner') throw new InboxBookingError(BOOKING_ERROR.paybackFromOwner);
  if (gross > owed) throw new InboxBookingError(BOOKING_ERROR.paybackTooLarge(Math.max(0, owed)));
  const counterparty = p.input.counterparty.trim() || p.original.meta?.counterparty || '';
  const label = `${p.original.series}${p.original.number}`;
  const rows = [
    { accountNumber: REIMBURSABLE_OWNER_ACCOUNT, debit: gross, credit: 0 },
    { accountNumber: p.source.accountNumber, debit: 0, credit: gross },
  ];
  const accountsToActivate = [...new Set(rows.map((r) => r.accountNumber))].filter((n) => !p.accounts.get(n)?.active).sort((a, b) => a - b);
  const meta: VerifikationMeta = {
    kind: 'settle',
    categoryId: null,
    paymentSourceId: p.input.paymentSourceId,
    counterparty,
    source: p.origin,
    reason: null,
    settlesVerifikationId: p.original.id,
    missingDocument: false,
    periodKey: p.periodKey,
  };
  const who = counterparty ? ` ${counterparty}` : '';
  const result: CompileResult = {
    draft: { date: p.input.date, description: `Återbetalning av utlägg${who} (${label})`, rows },
    meta,
    accountsToActivate,
    explanation: `${formatSek(gross)} kr betalas tillbaka till dig från ${p.source.name} och kvittar utlägget ${label}. Ingen kostnad, ingen intäkt, ingen moms.`,
  };
  return result;
}

/** Whether a `settle` against `original` is the 2893 payback rather than an invoice payment. */
export function isReimbursementTarget(original: Pick<Verifikation, 'rows'>): boolean {
  return reimbursementOwed(original) > 0 && !isOpenInvoiceShape(original);
}

/** Group the inbox list as the spec headings do; totals are proposal amounts. */
export function inboxGroups(items: readonly InboxItem[]): InboxGroup[] {
  const buckets: Record<InboxGroup['id'], InboxItem[]> = { ready: [], check: [], needs_you: [], bank_without_receipt: [], waiting: [] };
  for (const item of items) {
    switch (item.status) {
      case 'new':
      case 'processing':
        buckets.waiting.push(item);
        break;
      case 'ready':
      case 'check':
      case 'needs_you':
        if (item.kind === 'bank_row' && !item.attachmentId && item.proposal?.kind === 'purchase') buckets.bank_without_receipt.push(item);
        else buckets[item.status].push(item);
        break;
      case 'error':
        buckets.needs_you.push(item);
        break;
      case 'booked':
      case 'skipped':
      case 'private':
        break;
      default: {
        const exhaustive: never = item.status;
        throw new Error(`Unknown status ${String(exhaustive)}`);
      }
    }
  }
  const total = (list: InboxItem[]) => list.reduce((s, i) => s + (i.proposal?.amountOre ?? 0), 0);
  const make = (id: InboxGroup['id'], label: string, list: InboxItem[]): InboxGroup => ({ id, label, items: list, totalOre: total(list) });
  const ready = total(buckets.ready);
  return [
    make('ready', `Klara att bokföra (${buckets.ready.length} · ${formatWholeSek(ready)} kr)`, buckets.ready),
    make('check', `Kolla en sak (${buckets.check.length})`, buckets.check),
    make('needs_you', `Behöver dig (${buckets.needs_you.length})`, buckets.needs_you),
    make('bank_without_receipt', `Bankrader utan kvitto (${buckets.bank_without_receipt.length})`, buckets.bank_without_receipt),
    make('waiting', `Väntar på läsning (${buckets.waiting.length})`, buckets.waiting),
  ].filter((g) => g.items.length > 0);
}

// ---- Service ----------------------------------------------------------------------

export interface BookItemOptions extends BookItemInput {
  /** The user answered "Ja, uppdatera" to "Uppdatera regeln?" – replace a differing rule. */
  updateRule?: boolean | undefined;
  /** The batch reconciles once at its end instead of after every item. */
  skipReconcile?: boolean | undefined;
}

export interface BookItemOutcome {
  /** The verifikation booked — the first one, when the item booked several. */
  verifikation: Verifikation;
  /** Every verifikation the item booked (a payment settling two invoices books two). */
  verifikationer: Verifikation[];
  /** Merchant memory outcome (for "Förifyll … automatiskt nästa gång?"). */
  memory?: RememberResult | undefined;
  /**
   * Resolves once the archive copy has been filed — with the warning text if
   * that failed, null otherwise. The booking stands either way. The review
   * card does not wait for it: the verifikation is committed before this
   * starts, and a network round trip must not hold the card open.
   */
  filed: Promise<string | null>;
}

/** A chip edit on the review card: the fields of the sentence the user can set. */
export type ProposalEdit = Partial<Pick<Proposal, 'kind' | 'categoryId' | 'paymentSourceId' | 'vat' | 'amountOre' | 'date' | 'counterparty' | 'options'>>;

export interface InboxServiceExtras {
  /** `bookItem` with the memory outcome and follow-up warnings. */
  bookItemDetailed(input: BookItemOptions, db?: BookkeepingDB): Promise<BookItemOutcome>;
  /** Run the pipeline and get the summary (`process` resolves to void per contract). */
  processWithSummary(companyId: Uuid, opts?: ProcessOptions, db?: BookkeepingDB): Promise<ProcessSummary>;
  /**
   * Reconcile the company's unresolved items, bank rows and open postings
   * against each other without any AI call. Runs by itself after imports,
   * answers and bookings; exposed for callers that changed the facts some
   * other way.
   */
  reconcile(companyId: Uuid, db?: BookkeepingDB): Promise<{ changed: number }>;
  /** Back from a failed booking to its tier; refuses for booked items. */
  reopen(itemId: Uuid, db?: BookkeepingDB): Promise<void>;
  /** Back to `new` so the pipeline reads it again (the stored reading is dropped). */
  retry(itemId: Uuid, db?: BookkeepingDB): Promise<void>;
  /** "Privat – inte företagets" (receipts). */
  markPrivate(itemId: Uuid, db?: BookkeepingDB): Promise<void>;
  /** "Ja, koppla": link a bank row item to an existing verifikation; books nothing. */
  linkToVerifikation(itemId: Uuid, verifikationId: Uuid, db?: BookkeepingDB): Promise<void>;
  /** A late receipt for a "Bokför utan kvitto" booking: the item becomes the document link. */
  attachToVerifikation(itemId: Uuid, verifikationId: Uuid, db?: BookkeepingDB): Promise<void>;
  setNote(itemId: Uuid, note: string, db?: BookkeepingDB): Promise<void>;
  /** Apply the answer to the proposal's question and triage again. */
  answerQuestion(itemId: Uuid, answer: { field: ProposalQuestion['field']; value: string }, db?: BookkeepingDB): Promise<InboxItem>;
  /**
   * Persist a chip edit as the user's decision on those fields and triage
   * again. The reconciliation keeps decided fields over anything it rebuilds.
   */
  editProposal(itemId: Uuid, edit: ProposalEdit, db?: BookkeepingDB): Promise<InboxItem>;
  /** A receipt's provisional bank match is confirmed: no reconciliation reassigns the row. */
  confirmMatch(itemId: Uuid, db?: BookkeepingDB): Promise<InboxItem>;
  /** A receipt's provisional bank match is wrong: the row is released and never proposed for it again. */
  rejectMatch(itemId: Uuid, db?: BookkeepingDB): Promise<InboxItem>;
  /** The settlement or refund targets the user picked, with their amounts; an empty list clears the choice. */
  setAllocations(itemId: Uuid, allocations: ProposalAllocation[], db?: BookkeepingDB): Promise<InboxItem>;
  /**
   * A second document for the same event (a card slip next to the receipt):
   * `itemId` is folded into `intoItemId`, whose booking will carry both files.
   */
  mergeDocuments(itemId: Uuid, intoItemId: Uuid, db?: BookkeepingDB): Promise<InboxItem>;
  /** The existing rule for the item's merchant and how the given choice relates to it ("Uppdatera regeln?"). */
  ruleStatusFor(item: InboxItem, input: TransactionInput, db?: BookkeepingDB): Promise<{ rule: MerchantRule | undefined; decision: ReturnType<typeof ruleDecision> }>;
  /** Which invoice a `settle` proposal for a bank row would pay (the same ranking the pipeline used). */
  settleTargetFor(item: InboxItem, db?: BookkeepingDB): Promise<Uuid | undefined>;
  /** The `TransactionInput` an item's proposal describes (transfer/settle targets resolved); null when incomplete. */
  inputForItem(item: InboxItem, db?: BookkeepingDB): Promise<TransactionInput | null>;
  listForCompany(companyId: Uuid, db?: BookkeepingDB): Promise<InboxItem[]>;
}

/**
 * One pipeline run per company at a time, in this tab. A second `process`
 * while one runs waits for it and then runs itself — picking up whatever
 * arrived meanwhile — instead of reading the same receipts twice. The
 * reconciliation after an answer or a booking queues behind a run too, so
 * its writes never interleave with the run's phases.
 */
const runQueues = new Map<Uuid, Promise<unknown>>();

export async function withRunLock<T>(companyId: Uuid, fn: () => Promise<T>): Promise<T> {
  const previous = runQueues.get(companyId) ?? Promise.resolve();
  const next = previous.then(fn, fn);
  runQueues.set(companyId, next);
  try {
    return await next;
  } finally {
    if (runQueues.get(companyId) === next) runQueues.delete(companyId);
  }
}

export function createInboxService(deps: InboxDeps = {}): InboxService & InboxServiceExtras {
  const markDirty = (companyId: Uuid) => {
    try {
      deps.markDirty?.(companyId);
    } catch {
      /* the backup hook must never break the inbox */
    }
  };

  async function getItem(itemId: Uuid, db: BookkeepingDB): Promise<InboxItem> {
    const item = await db.inboxItems.get(itemId);
    if (!item) throw new InboxBookingError(BOOKING_ERROR.itemMissing);
    return item;
  }

  async function update(db: BookkeepingDB, id: Uuid, patch: ItemPatch): Promise<void> {
    await patchItem(db, id, patch);
  }

  async function addFiles(companyId: Uuid, files: InboxFileInput[], db: BookkeepingDB = defaultDb): Promise<InboxItem[]> {
    const out: InboxItem[] = [];
    for (const file of files) {
      const { attachment, duplicate, restoredBytes } = await storeAttachment(
        { companyId, blob: file.blob, name: file.name, driveFileId: file.driveFileId, alreadyShrunk: file.source === 'phone' },
        deps.media ?? {},
        db,
      );
      if (duplicate) {
        const existing = await db.inboxItems
          .where('companyId')
          .equals(companyId)
          .and((i) => i.attachmentId === attachment.id && i.status !== 'skipped' && i.status !== 'private')
          .first();
        if (existing) {
          const patch: Partial<InboxItem> = {};
          if (file.driveFileId && !existing.driveFileId) patch.driveFileId = file.driveFileId;
          // The item was parked on "filen saknas lokalt"; the file is here now,
          // so it goes back in the queue instead of staying parked next to it.
          if (restoredBytes && existing.status !== 'booked' && existing.status !== 'new') Object.assign(patch, { status: 'new' as const, error: undefined, tierReasons: [] });
          if (Object.keys(patch).length) await update(db, existing.id, patch);
          out.push({ ...existing, ...patch });
          continue;
        }
      }
      const item: InboxItem = {
        id: crypto.randomUUID(),
        companyId,
        kind: 'receipt',
        status: 'new',
        tierReasons: [],
        flags: [],
        attachmentId: attachment.id,
        ...(file.driveFileId ? { driveFileId: file.driveFileId } : {}),
        source: file.source,
        matchedBankRowIds: [],
        ...(file.note?.trim() ? { note: file.note.trim() } : {}),
        ...(file.batchId ? { batchId: file.batchId } : {}),
        createdAt: now(),
        updatedAt: now(),
      };
      await db.inboxItems.add(item);
      out.push(item);
    }
    if (out.length) markDirty(companyId);
    return out;
  }

  async function addBankRows(companyId: Uuid, rowIds: Uuid[], db: BookkeepingDB = defaultDb, batchId?: string): Promise<InboxItem[]> {
    return ensureBankRowItems(db, companyId, rowIds, batchId);
  }

  async function processWithSummary(companyId: Uuid, opts: ProcessOptions = {}, db: BookkeepingDB = defaultDb): Promise<ProcessSummary> {
    const summary = await withRunLock(companyId, () => runPipeline(companyId, { concurrency: opts.concurrency, signal: opts.signal }, deps, db));
    if (summary.processed || summary.changed) markDirty(companyId);
    return summary;
  }

  async function process(companyId: Uuid, opts: ProcessOptions = {}): Promise<void> {
    await processWithSummary(companyId, opts, defaultDb);
  }

  /** The reconciliation on its own (no AI), queued behind any run in progress. */
  async function reconcile(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<{ changed: number }> {
    const result = await withRunLock(companyId, async () => {
      const ctx = await loadCompanyContext(companyId, db);
      const r = await reconcileCompany(companyId, ctx, db, { today: (deps.today ?? undefined)?.() });
      for (const linked of r.linkedDocuments) {
        const item = await db.inboxItems.get(linked.itemId);
        if (item) await fileLateDocument(item, linked.verifikationId, deps, db);
      }
      return r;
    });
    if (result.changed) markDirty(companyId);
    return { changed: result.changed };
  }

  /** The best open item of the given kinds for a bank row item – the same ranking the pipeline used. */
  async function candidateFor(item: InboxItem, kinds: ReadonlySet<OpenItem['kind']>, db: BookkeepingDB): Promise<Candidate | undefined> {
    if (!item.bankRowId) return undefined;
    const row = await db.bankRows.get(item.bankRowId);
    if (!row) return undefined;
    const [verifikationer, bankRows, paymentSources, items] = await Promise.all([
      db.verifikationer.where('companyId').equals(item.companyId).toArray(),
      db.bankRows.where('companyId').equals(item.companyId).toArray(),
      db.paymentSources.where('companyId').equals(item.companyId).toArray(),
      db.inboxItems.where('companyId').equals(item.companyId).and((i) => !!i.verifikationId).toArray(),
    ]);
    const rejected = new Set(item.rejectedMatches ?? []);
    const open = deriveOpenItems({ verifikationer, bankRows, paymentSources, foreignCurrencyIds: foreignCurrencyIdsFrom(items) }).filter((o) => kinds.has(o.kind) && !rejected.has(o.verifikationId));
    const { auto, ask } = decide(rankCandidates({ ...row, status: 'open' }, open));
    return auto ?? ask[0];
  }

  const SETTLE_KINDS: ReadonlySet<OpenItem['kind']> = new Set<OpenItem['kind']>(['customer_invoice', 'supplier_invoice', 'reimbursement']);
  const ALL_KINDS: ReadonlySet<OpenItem['kind']> = new Set<OpenItem['kind']>(['settlement', 'customer_invoice', 'supplier_invoice', 'reimbursement']);

  /** Which invoice or utlägg a `settle` for the row pays: the proposal's own allocation first, else the ranking. */
  async function settleTargetFor(item: InboxItem, db: BookkeepingDB = defaultDb): Promise<Uuid | undefined> {
    const first = item.proposal?.allocations?.[0];
    if (first) return first.verifikationId;
    return (await candidateFor(item, SETTLE_KINDS, db))?.item.verifikationId;
  }

  /** "Betalning av faktura A7 (Telia)" / "Återbetalning av ditt utlägg A3 (Clas Ohlson)". */
  function settleReasonFor(c: Candidate, v: Verifikation | undefined): string {
    const label = v ? `${v.series}${v.number}` : '';
    return c.item.kind === 'reimbursement' ? REIMBURSEMENT_REASON(label || 'utlägget', c.item.counterparty) : INVOICE_SETTLEMENT_REASON(label || 'fakturan', c.item.counterparty);
  }

  /** The `TransactionInput` an item's proposal describes, with transfer/settle targets resolved; null when incomplete. */
  async function inputForItem(item: InboxItem, db: BookkeepingDB): Promise<TransactionInput | null> {
    const extra: { counterpartySourceId?: Uuid | undefined; settlesVerifikationId?: Uuid | undefined } = {};
    if (item.proposal?.kind === 'transfer') {
      const pairId = item.matchedBankRowIds[0];
      const pair = pairId ? await db.bankRows.get(pairId) : undefined;
      extra.counterpartySourceId = pair?.paymentSourceId;
    }
    if (item.proposal?.kind === 'settle') extra.settlesVerifikationId = await settleTargetFor(item, db);
    return transactionInputFromItem(item, extra);
  }

  async function completeInput(item: InboxItem, input: TransactionInput, db: BookkeepingDB): Promise<TransactionInput> {
    const next: TransactionInput = { ...input, documentIds: [...input.documentIds] };
    // A receipt item's own files are always its documents.
    for (const id of documentIdsOf(item)) if (!next.documentIds.includes(id)) next.documentIds.push(id);
    if (next.kind === 'transfer' && !next.counterpartySourceId) {
      const pairId = item.matchedBankRowIds[0];
      const pair = pairId ? await db.bankRows.get(pairId) : undefined;
      if (pair) next.counterpartySourceId = pair.paymentSourceId;
    }
    if (next.kind === 'settle' && !next.options?.settlesVerifikationId) {
      const target = await settleTargetFor(item, db);
      if (target) next.options = { ...(next.options ?? {}), settlesVerifikationId: target };
    }
    if (next.kind === 'refund' && !next.options?.refundOfVerifikationId) {
      const target = item.proposal?.allocations?.[0]?.verifikationId;
      if (target) next.options = { ...(next.options ?? {}), refundOfVerifikationId: target };
    }
    return next;
  }

  /**
   * The allocations a booking runs: the proposal's own for a settlement or a
   * refund (several for one payment settling several invoices), else the one
   * the input names. Each becomes its own verifikation.
   */
  function allocationsFor(item: InboxItem, input: TransactionInput): ProposalAllocation[] | null {
    if (input.kind === 'settle' || input.kind === 'refund') {
      const own = item.proposal?.allocations ?? [];
      if (own.length > 1) {
        if (own.reduce((s, a) => s + a.amountOre, 0) !== input.amountOre) throw new InboxBookingError(BOOKING_ERROR.allocationSum);
        return own;
      }
      const target = input.kind === 'settle' ? input.options?.settlesVerifikationId : input.options?.refundOfVerifikationId;
      return target ? [{ verifikationId: target, amountOre: input.amountOre }] : null;
    }
    return null;
  }

  /**
   * The transactional core: activate confirmed accounts, compile, book, link
   * bank rows, mark the item booked. Throws on any failure (rolled back).
   *
   * Under the shared ledger the whole transaction — activation, booking,
   * links, the item's status — is published to the remote copy before it
   * commits, and rerun on fresh numbers when another device got there first.
   */
  async function bookCore(item: InboxItem, input: TransactionInput, confirmedActivations: number[], db: BookkeepingDB): Promise<Verifikation[]> {
    return withSharedLedger(item.companyId, db, () => bookCoreLocally(item, input, confirmedActivations, db));
  }

  async function bookCoreLocally(item: InboxItem, input: TransactionInput, confirmedActivations: number[], db: BookkeepingDB): Promise<Verifikation[]> {
    return db.transaction(
      'rw',
      [db.inboxItems, db.bankRows, db.verifikationer, db.fiscalYears, db.accounts, db.accountEvents, db.periodLocks, db.companySettings, db.companies, db.paymentSources],
      async () => {
        /*
         * Idempotency lives here, inside the transaction, not in the caller:
         * a stale card, a double click or a second tab all reach this point
         * with the item they loaded a moment ago. What counts is the row in
         * the database now — an item booked meanwhile is not booked again,
         * and a bank row someone else linked is not linked twice.
         */
        const fresh = await db.inboxItems.get(item.id);
        if (!fresh) throw new InboxBookingError(BOOKING_ERROR.itemMissing);
        if (fresh.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.alreadyBooked);
        const ctx: CompanyContext = await loadCompanyContext(item.companyId, db);
        const fiscalYear = ctx.openYears.find((fy) => isWithin(input.date, fy));
        if (!fiscalYear) throw new InboxBookingError(BOOKING_ERROR.outsideFiscalYear);

        const bankRowIds = [...new Set([...(fresh.bankRowId ? [fresh.bankRowId] : []), ...fresh.matchedBankRowIds])];
        const rowsToLink = await db.bankRows.where('id').anyOf(bankRowIds).toArray();
        for (const row of rowsToLink) {
          if (row.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.bankRowAlreadyBooked);
          if (row.linkedVerifikationIds.length) {
            const other = await db.verifikationer.get(row.linkedVerifikationIds[0]!);
            throw new InboxBookingError(BOOKING_ERROR.bankRowTaken(other ? `${other.series}${other.number}` : 'en annan händelse'));
          }
        }

        // Accounts the UI confirmed activating.
        for (const number of confirmedActivations) {
          if (!ctx.accountMap.has(number)) await activateBasAccount({ companyId: item.companyId, number, fiscalYearId: fiscalYear.id }, db);
        }
        const afterActivation = confirmedActivations.length ? await loadCompanyContext(item.companyId, db) : ctx;
        const periodKey = periodKeyFor(input.date, fiscalYear, afterActivation.settings.vatPeriod);
        const origin: VerifikationMeta['source'] = fresh.kind === 'receipt' ? 'receipt' : 'bank';
        const merchantKey = input.counterparty ? merchantKeyOf(input.counterparty) : undefined;

        const allocations = allocationsFor(fresh, input);
        const parts: { input: TransactionInput; original: Verifikation | undefined }[] = [];
        if (allocations) {
          // What each target still owes is read here, inside the transaction:
          // a second click, or a payment booked on the other device, must not
          // settle the same invoice twice.
          const companyVerifikationer = await db.verifikationer.where('companyId').equals(item.companyId).toArray();
          const bankRows = await db.bankRows.where('companyId').equals(item.companyId).toArray();
          const linkedItems = await db.inboxItems.where('companyId').equals(item.companyId).and((i) => !!i.verifikationId).toArray();
          const open = deriveOpenItems({ verifikationer: companyVerifikationer, bankRows, paymentSources: afterActivation.paymentSources, foreignCurrencyIds: foreignCurrencyIdsFrom(linkedItems) });
          const refundable = refundablePurchases({ verifikationer: companyVerifikationer, paymentSources: afterActivation.paymentSources }, { paidFromBank: false });
          for (const a of allocations) {
            const original = await db.verifikationer.get(a.verifikationId);
            if (!original || original.companyId !== item.companyId) throw new InboxBookingError(BOOKING_ERROR.verifikationMissing);
            const label = `${original.series}${original.number}`;
            if (input.kind === 'refund') {
              const remaining = refundable.find((r) => r.verifikationId === a.verifikationId)?.remainingOre ?? 0;
              if (a.amountOre > remaining) throw new InboxBookingError(BOOKING_ERROR.refundTooLarge(label, Math.max(0, remaining)));
            } else if (!isReimbursementTarget(original)) {
              const remaining = open.filter((o) => o.verifikationId === a.verifikationId && (o.kind === 'supplier_invoice' || o.kind === 'customer_invoice')).reduce((s, o) => s + Math.abs(o.amountOre), 0);
              if (a.amountOre > remaining) throw new InboxBookingError(BOOKING_ERROR.allocationTooLarge(label, remaining));
            }
            const options = { ...(input.options ?? {}) };
            if (input.kind === 'settle') options.settlesVerifikationId = a.verifikationId;
            else options.refundOfVerifikationId = a.verifikationId;
            parts.push({ input: { ...input, amountOre: a.amountOre, options }, original });
          }
        } else {
          parts.push({ input, original: undefined });
        }

        const booked: Verifikation[] = [];
        for (const part of parts) {
          let compiled: CompileResult;
          const original = part.original;
          if (part.input.kind === 'settle' && original && isReimbursementTarget(original)) {
            // The payback of "Egna pengar" (D 2893 / K bank) – see compileReimbursementPayback.
            const source = afterActivation.sourceMap.get(part.input.paymentSourceId);
            if (!source) throw new InboxBookingError('Välj vilket konto pengarna betalades från.');
            const paybacks = await db.verifikationer.where('companyId').equals(item.companyId).and((v) => v.meta?.settlesVerifikationId === original.id && !v.correctsId).toArray();
            const alreadyPaidOre = paybacks.reduce((sum, v) => sum + grossOf(v), 0);
            compiled = compileReimbursementPayback({ input: part.input, source, original, alreadyPaidOre, accounts: afterActivation.accountMap, periodKey, origin });
          } else {
            compiled = compileTransaction(part.input, {
              entityType: afterActivation.company.entityType,
              vatRegistered: afterActivation.settings.vatRegistered,
              accountingMethod: afterActivation.settings.accountingMethod,
              accounts: afterActivation.accountMap,
              paymentSources: afterActivation.sourceMap,
              catalog: afterActivation.catalog,
              rulesYear: rulesYearOf(fiscalYear),
              ...(original ? { originalRows: original.rows } : {}),
              periodKey,
              source: origin,
            });
          }
          if (compiled.accountsToActivate.length) throw new InboxBookingError(BOOKING_ERROR.activationNotConfirmed(compiled.accountsToActivate));

          const key = compiled.meta.merchantKey ?? merchantKey;
          const meta: VerifikationMeta = {
            ...compiled.meta,
            reason: fresh.proposal?.reason ?? null,
            inboxItemId: fresh.id,
            ...(bankRowIds.length ? { bankRowIds } : {}),
            ...(key ? { merchantKey: key } : {}),
            ...(fresh.proposal && fresh.proposal.confidence > 0 ? { confidence: fresh.proposal.confidence } : {}),
            missingDocument: compiled.meta.missingDocument || (part.input.kind === 'purchase' && part.input.documentIds.length === 0),
          };
          booked.push(
            await bookVerifikation(
              {
                companyId: item.companyId,
                fiscalYearId: fiscalYear.id,
                date: compiled.draft.date,
                description: compiled.draft.description,
                rows: compiled.draft.rows,
                attachmentIds: part.input.documentIds,
                meta,
              },
              db,
            ),
          );
        }
        const first = booked[0]!;

        // Bank rows: the item's own row is booked by this verifikation; matched rows are reconciled against it.
        for (const row of rowsToLink) {
          const linked = [...row.linkedVerifikationIds];
          for (const v of booked) if (!linked.includes(v.id)) linked.push(v.id);
          // A bank-row item books every row it carries (both halves of a
          // transfer); a receipt's rows are reconciled against it.
          await db.bankRows.update(row.id, { status: fresh.kind === 'bank_row' ? 'booked' : 'linked', linkedVerifikationIds: linked });
        }
        const siblings = bankRowIds.length ? await db.inboxItems.where('bankRowId').anyOf(bankRowIds).toArray() : [];
        for (const s of siblings) {
          if (s.id !== item.id && OPEN_STATUSES.has(s.status)) {
            await update(db, s.id, { status: 'booked', verifikationId: first.id, tierReasons: [reconciledText(first)], error: undefined });
          }
        }
        // Documents folded into this one are booked with it.
        const merged = await db.inboxItems.where('companyId').equals(item.companyId).and((i) => i.mergedIntoItemId === item.id && i.status !== 'booked').toArray();
        for (const m of merged) await update(db, m.id, { status: 'booked', verifikationId: first.id, error: undefined });
        await update(db, item.id, {
          status: 'booked',
          verifikationId: first.id,
          ...(booked.length > 1 ? { verifikationIds: booked.map((v) => v.id) } : {}),
          error: undefined,
        });
        return booked;
      },
    );
  }

  async function bookItemDetailed(input: BookItemOptions, db: BookkeepingDB = defaultDb): Promise<BookItemOutcome> {
    const item = await getItem(input.itemId, db);
    if (item.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.alreadyBooked);
    let booked: Verifikation[];
    let tx: TransactionInput;
    try {
      tx = await completeInput(item, input.input, db);
      booked = await bookCore(item, tx, input.confirmedActivations, db);
    } catch (err) {
      const { message, technical } = bookingErrorMessage(err);
      // A booking refused because it already happened is not a failure to record on the item.
      if (!(err instanceof InboxBookingError && err.message === BOOKING_ERROR.alreadyBooked)) await update(db, item.id, { status: reopenedStatus(item), error: message });
      throw new InboxBookingError(message, technical);
    }
    const verifikation = booked[0]!;

    const outcome: Omit<BookItemOutcome, 'filed'> = { verifikation, verifikationer: booked };
    /*
     * Merchant memory. Learned from every booking rather than from a "Kom ihåg"
     * tick: deciding whether future receipts from a place will resemble this
     * one needs knowledge of the merchant's range and of how names are matched,
     * which is precisely what someone new to bookkeeping does not have. The
     * booking itself is the signal. A rule that later disagrees with a receipt
     * demotes the item to "check" and says so, and "Uppdatera regeln?" still
     * asks before a remembered choice is replaced — consent at a moment there
     * is something concrete to consent to.
     */
    const knownMerchant = input.bulk && tx.counterparty.trim() ? await findMerchantRule(item.companyId, merchantKeyOf(tx.counterparty), db) : undefined;
    if (tx.counterparty.trim() && tx.categoryId && verifikation.meta?.vat && (!input.bulk || knownMerchant)) {
      try {
        outcome.memory = await rememberMerchant(
          {
            companyId: item.companyId,
            merchantName: tx.counterparty,
            confirmed: { kind: tx.kind, categoryId: tx.categoryId, vat: verifikation.meta.vat, paymentSourceId: tx.paymentSourceId },
            createdFrom: item.proposal?.reason === 'suggestion' ? 'ai_confirmed' : 'user',
            replaceIfDifferent: input.updateRule === true,
          },
          db,
        );
      } catch {
        /* memory is a convenience; the booking stands */
      }
    }
    // Archive copy: move the cloud Inkorg file, or upload the local one. Not
    // awaited — see `BookItemOutcome.filed`. A failure is recorded on the
    // item, and the archive check retries it later.
    const filed = fileBookedReceipt(item, verifikation, tx.counterparty, tx.amountOre, deps, db)
      .then((): string | null => null)
      .catch(async (): Promise<string | null> => {
        await update(db, item.id, { error: BOOKING_ERROR.driveFiling });
        return BOOKING_ERROR.driveFiling;
      });
    markDirty(item.companyId);
    // What was just booked may be what an open bank row or a waiting receipt
    // was expecting; the pile settles it now rather than at the next import.
    if (!input.skipReconcile) await reconcile(item.companyId, db).catch(() => undefined);
    return { ...outcome, filed };
  }

  /** Book and file: the complete operation, for callers with no card to close. */
  async function bookItem(input: BookItemOptions, db: BookkeepingDB = defaultDb): Promise<Verifikation> {
    const outcome = await bookItemDetailed(input, db);
    await outcome.filed;
    return outcome.verifikation;
  }

  async function bookBatch(input: BookBatchInput, onProgress: (done: number, total: number) => void, db: BookkeepingDB = defaultDb): Promise<BookBatchResult> {
    const booked: Uuid[] = [];
    const total = input.itemIds.length;
    onProgress(0, total);
    // Validate up front: never book part of a batch that contains an ineligible item.
    const items: InboxItem[] = [];
    for (const id of input.itemIds) {
      const item = await db.inboxItems.get(id);
      if (!item) return { booked, failed: { itemId: id, error: BOOKING_ERROR.itemMissing } };
      // A repeated approval of something already booked is nothing to do, not a failure.
      if (item.status === 'booked') {
        booked.push(item.id);
        onProgress(booked.length, total);
        continue;
      }
      const settings = (await loadCompanyContext(item.companyId, db)).settings;
      if (item.tier === 'needs_you' || item.status === 'needs_you') return { booked, failed: { itemId: id, error: BOOKING_ERROR.batchNeedsYou } };
      if (!batchEligible(item, settings.batchThresholdOre)) {
        const amount = item.proposal?.amountOre ?? 0;
        return {
          booked,
          failed: { itemId: id, error: amount > settings.batchThresholdOre ? BOOKING_ERROR.batchAboveThreshold(settings.batchThresholdOre) : BOOKING_ERROR.incompleteProposal },
        };
      }
      items.push(item);
    }
    let companyId: Uuid | null = null;
    try {
      for (const item of items) {
        companyId = item.companyId;
        const tx = await inputForItem(item, db);
        if (!tx) {
          await update(db, item.id, { status: reopenedStatus(item), error: BOOKING_ERROR.incompleteProposal });
          return { booked, failed: { itemId: item.id, error: BOOKING_ERROR.incompleteProposal } };
        }
        try {
          await (await bookItemDetailed({ itemId: item.id, input: tx, confirmedActivations: input.confirmedActivations, bulk: true, skipReconcile: true }, db)).filed;
        } catch (err) {
          if (err instanceof InboxBookingError && err.message === BOOKING_ERROR.alreadyBooked) {
            booked.push(item.id);
            onProgress(booked.length, total);
            continue;
          }
          return { booked, failed: { itemId: item.id, error: bookingErrorMessage(err).message } };
        }
        booked.push(item.id);
        onProgress(booked.length, total);
      }
    } finally {
      if (companyId) await reconcile(companyId, db).catch(() => undefined);
    }
    return { booked };
  }

  async function releaseMatchedRows(item: InboxItem, db: BookkeepingDB): Promise<void> {
    if (!item.matchedBankRowIds.length) return;
    await addBankRows(item.companyId, item.matchedBankRowIds, db);
  }

  async function skip(itemId: Uuid, reason: SkipReason, db: BookkeepingDB = defaultDb): Promise<void> {
    const item = await getItem(itemId, db);
    if (item.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.alreadyBooked);
    // A company bank row is never ignored: "privat" books it against the owner (answerQuestion → owner_out).
    if (item.kind === 'bank_row') throw new InboxBookingError(BOOKING_ERROR.bankRowNeverIgnored);
    const status: InboxStatus = reason === 'private' ? 'private' : 'skipped';
    await update(db, item.id, { status, tierReasons: [SKIP_REASON_TEXT[reason]], matchedBankRowIds: [], error: undefined });
    await releaseMatchedRows(item, db);
    if (item.driveFileId && deps.fileRejected) {
      try {
        await deps.fileRejected({ companyId: item.companyId, driveFileId: item.driveFileId, date: item.reading?.date ?? null, reason: reason === 'private' ? 'private' : 'skipped' }, db);
      } catch {
        await update(db, item.id, { error: 'Filen kunde inte flyttas i arkivkopian. Den ligger kvar i Inkorg.' });
      }
    }
    markDirty(item.companyId);
    // The row it held is free again; another receipt may be waiting for it.
    if (item.matchedBankRowIds.length) await reconcile(item.companyId, db).catch(() => undefined);
  }

  async function markPrivate(itemId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
    await skip(itemId, 'private', db);
  }

  async function reopen(itemId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
    const item = await getItem(itemId, db);
    if (item.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.cannotReopenBooked);
    await update(db, item.id, { status: reopenedStatus(item), error: undefined });
  }

  async function retry(itemId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
    const item = await getItem(itemId, db);
    if (item.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.cannotReopenBooked);
    // "Läs igen" means a fresh reading: the stored one would otherwise be reused.
    await update(db, item.id, { status: 'new', error: undefined, errorTechnical: undefined, tierReasons: [], extraction: undefined, classification: undefined, mergedIntoItemId: undefined });
  }

  /** Turn a bank row item into the `settle` proposal that pays `v` (invoice or utlägg); books nothing yet. */
  async function proposeSettlement(item: InboxItem, v: Verifikation, kind: OpenItem['kind'], counterparty: string, db: BookkeepingDB): Promise<void> {
    const ctx = await loadCompanyContext(item.companyId, db);
    const row = item.bankRowId ? await db.bankRows.get(item.bankRowId) : undefined;
    const base: InboxProposal = item.proposal ? withoutTaxSubkind({ ...(item.proposal as InboxProposal) }) : {
      kind: 'settle',
      categoryId: null,
      paymentSourceId: row?.paymentSourceId ?? ctx.settings.defaultPaymentSourceId,
      vat: null,
      amountOre: row ? Math.abs(row.amountOre) : null,
      date: row ? (row.transactionDate ?? row.bookingDate) : null,
      counterparty,
      reason: null,
      confidence: 0,
    };
    const { question: _q, ...rest } = base;
    const amount = rest.amountOre ?? (row ? Math.abs(row.amountOre) : 0);
    const proposal: InboxProposal = {
      ...rest,
      kind: 'settle',
      categoryId: null,
      vat: null,
      counterparty: counterparty || rest.counterparty,
      reason: 'match',
      confidence: 1,
      allocations: [{ verifikationId: v.id, amountOre: amount }],
    };
    const label = `${v.series}${v.number}`;
    const reason = kind === 'reimbursement' ? REIMBURSEMENT_REASON(label, counterparty) : INVOICE_SETTLEMENT_REASON(label, counterparty);
    const result = triage(triageInput({ ...item, proposal }, proposal, ctx, {}));
    const patch: ItemPatch = { proposal, flags: result.flags, tier: result.tier, tierReasons: [reason, ...result.reasons], error: undefined, decided: withDecided(item, ['kind', 'allocations', 'match']) };
    if (OPEN_STATUSES.has(item.status)) patch.status = result.tier;
    await update(db, item.id, patch);
  }

  /**
   * "Ja, koppla". A booking that already moved money on the bank account is
   * linked (nothing booked). An open invoice or an utlägg paid with the
   * owner's money needs a `settle` booking, so the item becomes that proposal
   * instead of being silently linked.
   */
  async function linkToVerifikation(itemId: Uuid, verifikationId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
    const item = await getItem(itemId, db);
    const v = await db.verifikationer.get(verifikationId);
    if (!v || v.companyId !== item.companyId) throw new InboxBookingError(BOOKING_ERROR.verifikationMissing);
    if (item.kind === 'bank_row' && (isReimbursementTarget(v) || isOpenInvoiceShape(v))) {
      await proposeSettlement(item, v, isReimbursementTarget(v) ? 'reimbursement' : 'supplier_invoice', v.meta?.counterparty ?? '', db);
      markDirty(item.companyId);
      return;
    }
    await db.transaction('rw', db.inboxItems, db.bankRows, db.verifikationer, async () => {
      if (item.bankRowId) {
        const row = await db.bankRows.get(item.bankRowId);
        if (row) await linkRow(db, row, verifikationId, item.id);
      }
      await update(db, item.id, { status: 'booked', verifikationId, tierReasons: [reconciledText(v)], error: undefined });
    });
    markDirty(item.companyId);
  }

  async function attachToVerifikation(itemId: Uuid, verifikationId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
    const item = await getItem(itemId, db);
    const v = await db.verifikationer.get(verifikationId);
    if (!v || v.companyId !== item.companyId) throw new InboxBookingError(BOOKING_ERROR.verifikationMissing);
    await update(db, item.id, { status: 'booked', verifikationId, tierReasons: [`Kopplat som kvitto till ${v.series}${v.number}`], error: undefined, matchedBankRowIds: [] });
    // A row this receipt held provisionally is free for the pile again.
    if (item.matchedBankRowIds.length) await releaseMatchedRows(item, db);
    try {
      await fileBookedReceipt(item, v, v.meta?.counterparty ?? '', grossOf(v), deps, db);
    } catch {
      await update(db, item.id, { error: BOOKING_ERROR.driveFiling });
    }
    markDirty(item.companyId);
    if (item.matchedBankRowIds.length) await reconcile(item.companyId, db).catch(() => undefined);
  }

  async function split(itemId: Uuid, parts: { amountOre: Ore; categoryId: string }[], db: BookkeepingDB = defaultDb): Promise<InboxItem[]> {
    const item = await getItem(itemId, db);
    if (item.status === 'booked') throw new InboxBookingError(BOOKING_ERROR.alreadyBooked);
    if (parts.length < 2 || parts.some((p) => !Number.isInteger(p.amountOre) || p.amountOre <= 0)) throw new InboxBookingError(BOOKING_ERROR.splitParts);
    const total = item.proposal?.amountOre ?? item.reading?.totalOre ?? null;
    const sum = parts.reduce((s, p) => s + p.amountOre, 0);
    if (total === null || sum !== total) throw new InboxBookingError(BOOKING_ERROR.splitSum);
    const ctx = await loadCompanyContext(item.companyId, db);
    return db.transaction('rw', db.inboxItems, async () => {
      const created: InboxItem[] = [];
      parts.forEach((part, index) => {
        const base = item.proposal ?? {
          kind: 'purchase' as const,
          categoryId: null,
          paymentSourceId: ctx.settings.defaultPaymentSourceId,
          vat: null,
          amountOre: total,
          date: item.reading?.date ?? null,
          counterparty: item.reading?.merchant ?? '',
          reason: null,
          confidence: 0,
        };
        const { question: _q, allocations: _a, ...rest } = base;
        // The printed VAT belongs to the whole receipt; each part gets the rate and lets the compiler split its own gross.
        const vat = rest.vat ? { rate: rest.vat.rate, mode: rest.vat.mode } : null;
        const proposal: Proposal = { ...rest, vat, amountOre: part.amountOre, categoryId: part.categoryId, reason: null, confidence: 1 };
        const probe: InboxItem = {
          id: crypto.randomUUID(),
          companyId: item.companyId,
          kind: 'receipt',
          status: 'new',
          tierReasons: [],
          flags: item.flags.filter((f) => f !== 'duplicate'),
          ...(item.attachmentId ? { attachmentId: item.attachmentId } : {}),
          ...(index === 0 && item.driveFileId ? { driveFileId: item.driveFileId } : {}),
          source: item.source,
          ...(item.reading ? { reading: item.reading } : {}),
          proposal,
          matchedBankRowIds: [],
          // Every field of a part is the user's: a reconciliation must not rebuild it from the whole receipt.
          decided: ['kind', 'categoryId', 'paymentSourceId', 'vat', 'amountOre', 'date', 'counterparty'],
          ...(item.batchId ? { batchId: item.batchId } : {}),
          note: `Del ${index + 1} av ${parts.length}${item.note ? ` · ${item.note}` : ''}`,
          createdAt: now(),
          updatedAt: now(),
        };
        // Triaged on the proposal alone: the reading's totals and VAT lines describe the whole receipt, not the part.
        const result = triage(triageInput(probe, proposal, ctx, {}));
        created.push({ ...probe, status: result.tier, tier: result.tier, tierReasons: result.reasons, flags: result.flags });
      });
      await db.inboxItems.bulkAdd(created);
      await update(db, item.id, { status: 'skipped', tierReasons: [`Delad i ${parts.length} köp`], error: undefined, matchedBankRowIds: [] });
      return created;
    });
  }

  /** Re-triage an item on its current proposal and write the result; `leading` reasons go first. */
  async function retriage(item: InboxItem, proposal: InboxProposal, patch: ItemPatch, leading: string[], db: BookkeepingDB, forced?: string): Promise<InboxItem> {
    const ctx = await loadCompanyContext(item.companyId, db);
    const matched = patch.matchedBankRowIds ?? item.matchedBankRowIds;
    const probe: InboxItem = { ...item, flags: patch.flags ?? item.flags, proposal, matchedBankRowIds: matched };
    const result = triage(triageInput(probe, proposal, ctx, { reading: item.reading, duplicateOf: item.duplicateOfVerifikationId }));
    let tier = result.tier;
    let reasons = [...leading, ...result.reasons];
    if (forced) {
      tier = 'needs_you';
      reasons = [forced, ...reasons.filter((r) => r !== TRIAGE_REASON.lowConfidence)];
    }
    const next: ItemPatch = { ...patch, proposal, flags: result.flags, tier, tierReasons: [...new Set(reasons)], matchedBankRowIds: matched, error: undefined };
    if (OPEN_STATUSES.has(item.status)) next.status = tier;
    await update(db, item.id, next);
    return getItem(item.id, db);
  }

  /**
   * Apply the answer to the proposal's one question (VAT chip, the section 9
   * cards, category/payment picks, the pile's match questions) and triage
   * again. What the answer settled is recorded in `decided`, and what it
   * ruled out in `rejectedMatches`, so no reconciliation asks again. `persons`
   * answers are not stored on the proposal; the UI passes them as
   * `options.persons`.
   */
  async function answerQuestion(itemId: Uuid, answer: { field: ProposalQuestion['field']; value: string }, db: BookkeepingDB = defaultDb): Promise<InboxItem> {
    const item = await getItem(itemId, db);
    if (!item.proposal) throw new InboxBookingError(BOOKING_ERROR.incompleteProposal);
    const applied = applyAnswer(item.proposal, answer);
    if (applied.keepQuestion) return item;
    const ctx = await loadCompanyContext(item.companyId, db);
    let proposal = applied.proposal;
    let matched = item.matchedBankRowIds;
    let forced: string | undefined;
    const leading: string[] = [];
    const patch: ItemPatch = { decided: withDecided(item, applied.decided) };
    let rejected = [...(item.rejectedMatches ?? [])];
    const reject = (ids: readonly Uuid[]) => {
      for (const id of ids) if (!rejected.includes(id)) rejected.push(id);
    };
    let needsReconcile = false;

    switch (applied.effect.type) {
      case 'none':
        break;
      case 'link': {
        const c = await candidateFor(item, ALL_KINDS, db);
        if (!c) {
          proposal = { ...proposal, reason: null, confidence: 0 };
          forced = ANSWER_TEXT.noCandidateFound;
          break;
        }
        const v = await db.verifikationer.get(c.item.verifikationId);
        if (!v) throw new InboxBookingError(BOOKING_ERROR.verifikationMissing);
        if (c.item.kind === 'settlement') {
          await linkToVerifikation(item.id, v.id, db);
          return getItem(item.id, db);
        }
        const amount = proposal.amountOre ?? Math.abs(c.item.amountOre);
        proposal = {
          ...withoutTaxSubkind(proposal),
          kind: 'settle',
          categoryId: null,
          vat: null,
          counterparty: c.item.counterparty || proposal.counterparty,
          reason: 'match',
          confidence: 1,
          allocations: [{ verifikationId: c.item.verifikationId, amountOre: Math.min(amount, Math.abs(c.item.amountOre)) }],
        };
        patch.decided = withDecided({ decided: patch.decided ?? [] }, ['kind', 'allocations', 'match']);
        leading.push(settleReasonFor(c, v));
        break;
      }
      case 'reject_candidate': {
        // "Nej, visa fler": the candidate on the card is out; the reconciliation finds the next or falls through.
        reject((item.proposal.allocations ?? []).map((a) => a.verifikationId));
        needsReconcile = true;
        break;
      }
      case 'release_pair': {
        // "Nej" to an own transfer: the row is a plain purchase or sale from
        // now on (the user's word), and the other row is free again.
        const row = item.bankRowId ? await db.bankRows.get(item.bankRowId) : undefined;
        proposal = { ...withoutTaxSubkind(proposal), kind: row && row.amountOre > 0 ? 'sale' : 'purchase' };
        patch.decided = withDecided({ decided: patch.decided ?? [] }, ['kind']);
        if (matched.length) {
          reject(matched);
          await addBankRows(item.companyId, matched, db);
          matched = [];
        }
        needsReconcile = true;
        break;
      }
      case 'settle_target': {
        const c = await candidateFor(item, SETTLE_KINDS, db);
        if (!c) {
          proposal = { ...proposal, reason: null, confidence: 0 };
          forced = ANSWER_TEXT.noInvoiceFound;
          break;
        }
        const v = await db.verifikationer.get(c.item.verifikationId);
        const amount = proposal.amountOre ?? Math.abs(c.item.amountOre);
        proposal = { ...proposal, counterparty: c.item.counterparty || proposal.counterparty, reason: 'match', confidence: 1, allocations: [{ verifikationId: c.item.verifikationId, amountOre: Math.min(amount, Math.abs(c.item.amountOre)) }] };
        patch.decided = withDecided({ decided: patch.decided ?? [] }, ['allocations']);
        leading.push(settleReasonFor(c, v));
        break;
      }
      case 'confirm_allocations':
        leading.push(...item.tierReasons.filter((r) => r !== TRIAGE_REASON.question));
        break;
      case 'reject_allocations':
        reject((item.proposal.allocations ?? []).map((a) => a.verifikationId));
        needsReconcile = true;
        break;
      case 'match_row': {
        const row = await db.bankRows.get(applied.effect.rowId);
        if (!row || row.companyId !== item.companyId) throw new InboxBookingError(BANK_ROW_MISSING);
        matched = [row.id];
        proposal = { ...proposal, paymentSourceId: row.paymentSourceId, reason: 'match' };
        const foreign = !!item.reading?.currency && item.reading.currency.toUpperCase() !== 'SEK';
        if (foreign && !isDecided({ decided: patch.decided ?? [] }, 'amountOre')) proposal.amountOre = Math.abs(row.amountOre);
        if (proposal.date) leading.push(TRIAGE_REASON.match(row.transactionDate ?? row.bookingDate, proposal.date));
        leading.push(DECISION_TEXT.matchConfirmed);
        // The row's own item, if any, is folded into this receipt.
        await retireBankRowItems(db, [row.id]);
        needsReconcile = true;
        break;
      }
      case 'reject_rows':
        reject(applied.effect.rowIds);
        needsReconcile = true;
        break;
      case 'match_target': {
        const v = await db.verifikationer.get(applied.effect.verifikationId);
        if (!v || v.companyId !== item.companyId) throw new InboxBookingError(BOOKING_ERROR.verifikationMissing);
        leading.push(`Kreditnota som minskar köpet ${v.series}${v.number}`);
        break;
      }
      case 'reject_targets':
        reject(applied.effect.verifikationIds);
        needsReconcile = true;
        break;
      case 'needs_you':
        forced = applied.effect.reason;
        break;
      default: {
        const exhaustive: never = applied.effect;
        throw new Error(`Unknown answer effect ${String(exhaustive)}`);
      }
    }

    // A plain "tax" without the subkind still needs the Skatteverket card.
    if (proposal.kind === 'tax' && !taxSubkindOf(proposal) && !forced) proposal = { ...proposal, question: cardQuestion(taxQuestion(ctx.company.entityType)) };

    patch.flags = answer.field === 'vat' ? item.flags.filter((f) => f !== 'no_vat_breakdown') : item.flags;
    patch.matchedBankRowIds = matched;
    if (rejected.length) patch.rejectedMatches = rejected;
    const written = await retriage(item, proposal, patch, leading, db, forced);
    markDirty(item.companyId);
    if (needsReconcile) {
      await reconcile(item.companyId, db).catch(() => undefined);
      return getItem(item.id, db);
    }
    return written;
  }

  /** A chip edit: the user's word on those fields, from now on. */
  async function editProposal(itemId: Uuid, edit: ProposalEdit, db: BookkeepingDB = defaultDb): Promise<InboxItem> {
    const item = await getItem(itemId, db);
    if (!OPEN_STATUSES.has(item.status)) throw new InboxBookingError(BOOKING_ERROR.cannotReopenBooked);
    const ctx = await loadCompanyContext(item.companyId, db);
    const base: InboxProposal = item.proposal
      ? { ...(item.proposal as InboxProposal) }
      : {
          kind: 'purchase',
          categoryId: null,
          paymentSourceId: ctx.settings.defaultPaymentSourceId,
          vat: null,
          amountOre: item.reading && item.reading.currency.toUpperCase() === 'SEK' ? item.reading.totalOre : null,
          date: item.reading?.date ?? null,
          counterparty: item.reading?.merchant ?? '',
          reason: null,
          confidence: 0,
        };
    const fields: DecidedField[] = [];
    let proposal: InboxProposal = { ...base };
    const set = <K extends keyof ProposalEdit>(key: K, field: DecidedField) => {
      if (!(key in edit)) return;
      const value = edit[key];
      if (value === undefined) delete proposal[key];
      else (proposal as unknown as Record<string, unknown>)[key] = value;
      fields.push(field);
    };
    set('kind', 'kind');
    set('categoryId', 'categoryId');
    set('paymentSourceId', 'paymentSourceId');
    set('vat', 'vat');
    set('amountOre', 'amountOre');
    set('date', 'date');
    set('counterparty', 'counterparty');
    set('options', 'options');
    if (!fields.length) return item;
    if (edit.kind !== undefined) {
      if (!kindBooksOnCategory(edit.kind)) proposal = { ...proposal, categoryId: null, vat: null };
      if (edit.kind !== 'settle' && edit.kind !== 'refund') {
        const { allocations: _a, ...rest } = proposal;
        proposal = rest;
      }
      if (edit.kind !== 'tax') proposal = withoutTaxSubkind(proposal);
    }
    if (proposal.question && (fields.includes('kind') || (proposal.question.field === 'category' && fields.includes('categoryId')) || (proposal.question.field === 'vat' && fields.includes('vat')) || (proposal.question.field === 'payment' && fields.includes('paymentSourceId')))) {
      const { question: _q, ...rest } = proposal;
      proposal = rest;
    }
    if ((fields.includes('categoryId') || fields.includes('kind')) && proposal.reason !== 'match') proposal.reason = null;
    const patch: ItemPatch = { decided: withDecided(item, fields) };
    if (fields.includes('vat')) patch.flags = item.flags.filter((f) => f !== 'no_vat_breakdown');
    const leading = item.tierReasons.filter((r) => r.startsWith('Matchad mot') || r.startsWith('Förifyllt') || r === DECISION_TEXT.matchConfirmed);
    const written = await retriage(item, proposal, patch, leading, db);
    markDirty(item.companyId);
    // A changed amount or date can pair the receipt with another row.
    if (fields.includes('amountOre') || fields.includes('date') || fields.includes('counterparty') || fields.includes('paymentSourceId')) {
      await reconcile(item.companyId, db).catch(() => undefined);
      return getItem(item.id, db);
    }
    return written;
  }

  async function confirmMatch(itemId: Uuid, db: BookkeepingDB = defaultDb): Promise<InboxItem> {
    const item = await getItem(itemId, db);
    if (!item.proposal || !item.matchedBankRowIds.length) return item;
    const leading = [...item.tierReasons.filter((r) => r !== DECISION_TEXT.matchConfirmed), DECISION_TEXT.matchConfirmed];
    const written = await retriage(item, item.proposal as InboxProposal, { decided: withDecided(item, ['match']) }, leading, db);
    markDirty(item.companyId);
    return written;
  }

  async function rejectMatch(itemId: Uuid, db: BookkeepingDB = defaultDb): Promise<InboxItem> {
    const item = await getItem(itemId, db);
    if (!item.proposal) return item;
    const rows = item.matchedBankRowIds;
    if (!rows.length) return item;
    const rejected = [...(item.rejectedMatches ?? [])];
    for (const id of rows) if (!rejected.includes(id)) rejected.push(id);
    const decided = (item.decided ?? []).filter((f) => f !== 'match');
    // Back to what the document says: the row's account and amount were the match's.
    const extraction = item.extraction?.proposal;
    let proposal: InboxProposal = { ...(item.proposal as InboxProposal), reason: extraction ? 'suggestion' : null };
    const foreign = !!item.reading?.currency && item.reading.currency.toUpperCase() !== 'SEK';
    if (foreign && !decided.includes('amountOre')) proposal.amountOre = null;
    if (!decided.includes('paymentSourceId') && item.reading) {
      const ctx = await loadCompanyContext(item.companyId, db);
      proposal = { ...proposal, paymentSourceId: paymentSourceForHint(item.reading.paymentHint, ctx.paymentSources, ctx.settings) };
    }
    await addBankRows(item.companyId, rows, db);
    const leading = [DECISION_TEXT.matchRejected, ...item.tierReasons.filter((r) => !r.startsWith('Matchad mot') && r !== DECISION_TEXT.matchConfirmed && r !== DECISION_TEXT.matchRejected)];
    await retriage(item, proposal, { decided, rejectedMatches: rejected, matchedBankRowIds: [] }, leading, db);
    markDirty(item.companyId);
    await reconcile(item.companyId, db).catch(() => undefined);
    return getItem(item.id, db);
  }

  async function setAllocations(itemId: Uuid, allocations: ProposalAllocation[], db: BookkeepingDB = defaultDb): Promise<InboxItem> {
    const item = await getItem(itemId, db);
    if (!item.proposal) throw new InboxBookingError(BOOKING_ERROR.incompleteProposal);
    if (!allocations.length) {
      // "Ändra": back to no target chosen, and the pile may propose one again.
      const { allocations: _a, question: _q, ...rest } = item.proposal as InboxProposal;
      const decided = (item.decided ?? []).filter((f) => f !== 'allocations' && f !== 'match');
      const cleared: InboxProposal = { ...rest, reason: null, confidence: 0 };
      const written = await retriage(item, cleared, { decided, matchedBankRowIds: item.kind === 'receipt' ? item.matchedBankRowIds : [] }, [], db);
      markDirty(item.companyId);
      await reconcile(item.companyId, db).catch(() => undefined);
      return (await db.inboxItems.get(item.id)) ?? written;
    }
    if (allocations.some((a) => !Number.isInteger(a.amountOre) || a.amountOre <= 0)) throw new InboxBookingError(BOOKING_ERROR.allocationSum);
    const labels: string[] = [];
    for (const a of allocations) {
      const v = await db.verifikationer.get(a.verifikationId);
      if (!v || v.companyId !== item.companyId) throw new InboxBookingError(BOOKING_ERROR.verifikationMissing);
      labels.push(`${v.series}${v.number}`);
    }
    const kind = item.proposal.kind === 'refund' ? 'refund' : 'settle';
    const { question: _q, ...rest } = item.proposal as InboxProposal;
    const proposal: InboxProposal = { ...withoutTaxSubkind(rest), kind, categoryId: kind === 'refund' ? rest.categoryId : null, vat: kind === 'refund' ? rest.vat : null, allocations, reason: 'match', confidence: 1 };
    const leading = [kind === 'refund' ? `Återbetalning av köpet ${labels.join(', ')}` : labels.length === 1 ? `Betalning av faktura ${labels[0]}` : `Betalning av fakturorna ${labels.join(', ')}`];
    const written = await retriage(item, proposal, { decided: withDecided(item, ['kind', 'allocations', 'match']) }, leading, db);
    markDirty(item.companyId);
    await reconcile(item.companyId, db).catch(() => undefined);
    return written;
  }

  async function mergeDocuments(itemId: Uuid, intoItemId: Uuid, db: BookkeepingDB = defaultDb): Promise<InboxItem> {
    if (itemId === intoItemId) throw new InboxBookingError(BOOKING_ERROR.mergeSelf);
    const [item, into] = await Promise.all([getItem(itemId, db), getItem(intoItemId, db)]);
    if (item.companyId !== into.companyId || item.kind !== 'receipt' || into.kind !== 'receipt') throw new InboxBookingError(BOOKING_ERROR.mergeClosed);
    if (!OPEN_STATUSES.has(into.status) || !OPEN_STATUSES.has(item.status)) throw new InboxBookingError(BOOKING_ERROR.mergeClosed);
    const extra = [...new Set([...(into.extraAttachmentIds ?? []), ...documentIdsOf(item)])].filter((id) => id !== into.attachmentId);
    const title = into.proposal?.counterparty || into.reading?.merchant || 'kvittot';
    await db.transaction('rw', db.inboxItems, async () => {
      await update(db, into.id, { extraAttachmentIds: extra, flags: into.flags.filter((f) => f !== 'duplicate'), duplicateOfVerifikationId: undefined });
      await update(db, item.id, { status: 'skipped', tierReasons: [DECISION_TEXT.mergedInto(title)], mergedIntoItemId: into.id, matchedBankRowIds: [], flags: item.flags.filter((f) => f !== 'duplicate'), error: undefined });
    });
    if (item.matchedBankRowIds.length) await releaseMatchedRows(item, db);
    markDirty(item.companyId);
    await reconcile(item.companyId, db).catch(() => undefined);
    return getItem(into.id, db);
  }

  async function setNote(itemId: Uuid, note: string, db: BookkeepingDB = defaultDb): Promise<void> {
    await getItem(itemId, db);
    await db.inboxItems.update(itemId, { note: note.trim(), updatedAt: now() });
  }

  async function ruleStatusFor(item: InboxItem, input: TransactionInput, db: BookkeepingDB = defaultDb) {
    const rule = input.counterparty ? await findMerchantRule(item.companyId, merchantKeyOf(input.counterparty), db) : undefined;
    const vat = input.vat ?? (input.categoryId ? findCategory((await loadCompanyContext(item.companyId, db)).catalog, input.categoryId)?.defaultVat : undefined);
    const decision = ruleDecision(rule, {
      kind: input.kind,
      categoryId: input.categoryId ?? '',
      vat: vat ?? { rate: 0, mode: 'none' },
      paymentSourceId: input.paymentSourceId,
    });
    return { rule, decision };
  }

  async function listForCompany(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<InboxItem[]> {
    return db.inboxItems.where('companyId').equals(companyId).sortBy('createdAt');
  }

  return {
    addFiles,
    addBankRows,
    process,
    processWithSummary,
    reconcile,
    bookItem,
    bookItemDetailed,
    bookBatch,
    skip,
    split,
    reopen,
    retry,
    markPrivate,
    linkToVerifikation,
    attachToVerifikation,
    setNote,
    answerQuestion,
    editProposal,
    confirmMatch,
    rejectMatch,
    setAllocations,
    mergeDocuments,
    ruleStatusFor,
    settleTargetFor,
    inputForItem,
    listForCompany,
  };
}

/** App-wide dependencies, wired once at start-up (`configureInboxDeps`). */
const appDeps: InboxDeps = {};

/** Wire AI, Drive, backup and media into the shared `inboxService`. */
export function configureInboxDeps(deps: InboxDeps): void {
  Object.assign(appDeps, deps);
}

/**
 * Which dependencies are currently wired. The pipeline degrades silently to
 * "Kvittona väntar på AI" when `readReceipt` is missing, so the start-up test
 * asserts on this rather than on a network call.
 */
export function wiredInboxDeps(): readonly (keyof InboxDeps)[] {
  return Object.keys(appDeps) as (keyof InboxDeps)[];
}

/** The shared service used by the UI; tests use `createInboxService(deps)` with a fresh db. */
export const inboxService: InboxService & InboxServiceExtras = createInboxService(appDeps);
